import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  Select,
  TextArea,
  PhoneInput,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneAdvertisement } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import {
  FiImage,
  FiClock,
  FiDollarSign,
  FiAlignLeft,
  FiSettings,
  FiPhone,
} from "react-icons/fi";
import { FaBullhorn } from "react-icons/fa";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const advertisement: any = getOneAdvertisement();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [thumbnail, setThumbnail] = useState<any>(advertisement?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(
    advertisement?.image_cdn_id
  );
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const dosee_ads_id = advertisement?.dosee_ads_id;
  const [title, setTitle] = useState<any>(advertisement?.title);
  const [startDate, setStartDate] = useState<any>(advertisement?.start_date);
  const [endDate, setEndDate] = useState<any>(advertisement?.end_date);
  const [startTime, setStartTime] = useState<any>(advertisement?.start_time);
  const [endTime, setEndTime] = useState<any>(advertisement?.end_time);
  const [price, setFee] = useState<any>(advertisement?.price);
  const [email, setEmail] = useState<any>(advertisement?.email_address);
  const [website, setWebsite] = useState<any>(advertisement?.website);
  const [phoneNumber, setPhoneNumber] = useState<any>(
    advertisement?.phone_number
  );
  const [currency, setCurrency] = useState<any>(advertisement?.currency);
  const [advStatus, setAdvStatus] = useState<any>(advertisement?.status);
  const [description, setDescription] = useState<any>(advertisement?.description);
  const advertisementRoutes = "/data/advertisement";

  const responses = (error: any, message: any) => {
    setError(error);
    setLoading(false);
    setSuccess(!error);
    setErrorMessage(message);
  };

  const updateOneAdvertisementHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : advertisement?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title?.toLowerCase()?.replace(/\s/g, "_"),
      description: description,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      price: price,
      website: website,
      currency: currency,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      phone_number: phoneNumber,
      email_address: email,
      status: advStatus,
    };

    const { message, status } = await updateOneContent({
      data_id: dosee_ads_id,
      data: data,
      content: _TYPES_.ADV,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={advertisementRoutes}
      eyebrow="DoSee Data • Ads"
      title="Edit Advertisement"
      subtitle={`Update an advertisement for ${entry_label || "this destination"}.`}
      icon={<FaBullhorn className="h-5 w-5" />}
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneAdvertisementHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Ad Creative"
        hint="High-resolution image."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.ADV)}
        />
      </AddSection>

      <AddSection title="Ad Details" hint="Title that appears with the ad.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Run Schedule"
        hint="When the ad is active."
        icon={<FiClock className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <InputField
            props={{
              id: "start_date",
              title: "Start Date",
              type: "date",
              required: true,
              value: startDate || "",
              onChange: (e: any) => {
                setStartDate(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "end_date",
              title: "End Date",
              type: "date",
              required: true,
              value: endDate || "",
              onChange: (e: any) => {
                setEndDate(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "start_time",
              title: "Start Time",
              type: "time",
              value: startTime || "",
              onChange: (e: any) => {
                setStartTime(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "end_time",
              title: "End Time",
              type: "time",
              value: endTime || "",
              onChange: (e: any) => {
                setEndTime(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Entrance / promo price."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputField
            props={{
              id: "price",
              title: "Price (figures only)",
              type: "number",
              value: price || "",
              onChange: (e: any) => {
                setFee(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "currency",
              title: "Currency",
              type: "text",
              value: currency || "",
              onChange: (e: any) => {
                setCurrency(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Contact Info"
        hint="How to reach the advertiser."
        icon={<FiPhone className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <InputField
            props={{
              id: "website",
              title: "Website",
              type: "text",
              required: true,
              value: website || "",
              onChange: (e: any) => {
                setWebsite(e.target.value);
                clearMessages();
              },
            }}
          />
          <PhoneInput
            setPhone={setPhoneNumber}
            phone={phoneNumber}
            required={false}
          />
          <InputField
            props={{
              id: "email",
              title: "Email Address",
              type: "email",
              value: email || "",
              onChange: (e: any) => {
                setEmail(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Visibility"
        hint="Toggle visibility of the ad."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Select
            props={{
              id: "advertisement_status",
              title: "Status",
              option: ["visible", "invisible"],
              preselect:
                advStatus !== undefined && advStatus !== null
                  ? [{ name: advStatus === 1 ? "visible" : "invisible" }]
                  : null,
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setAdvStatus(1);
                else setAdvStatus(0);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="What the ad is offering."
        icon={<FiAlignLeft className="h-4 w-4" />}
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            value: description || "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              clearMessages();
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Edit;
